import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ExamPreviewSkeleton,
  ExamPreviewTemplate,
  useExamDetail,
  useExamQuestions,
} from '@/features/exam-management';
import { useExamQuestionPreview } from '@/features/exam-management/hooks/useExamQuestionPreview';
import { EmptyState } from '@/shared/ui/molecules/EmptyState';

export const Route = createFileRoute('/_authenticated/exams/$id/preview/' as never)({
  component: ExamPreviewPage,
});

function ExamPreviewPage() {
  const { id } = (Route as unknown as { useParams: () => { id: string } }).useParams();
  const { t } = useTranslation('examManagement');

  const { exam, languages, isLoading } = useExamDetail(id);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedLangId, setSelectedLangId] = useState<number>(0);

  const defaultLang = languages.find(l => l.cultureInfo === 'pt-BR') ?? languages[0] ?? null;

  useEffect(() => {
    if (selectedLangId === 0 && defaultLang?.id) {
      setSelectedLangId(defaultLang.id);
    }
  }, [defaultLang, selectedLangId]);

  const activeLangId = selectedLangId !== 0 ? selectedLangId : (defaultLang?.id ?? 0);

  const { questions, isLoading: isLoadingQuestions } = useExamQuestions(activeLangId);

  const currentQuestion = questions[currentIndex] ?? null;

  const { preview, isLoading: isLoadingPreview } = useExamQuestionPreview(currentQuestion?.id ?? 0);

  function handleLanguageChange(langId: number) {
    setSelectedLangId(langId);
    setCurrentIndex(0);
  }

  if (isLoading || isLoadingQuestions) {
    return <ExamPreviewSkeleton />;
  }

  if (!exam) {
    return <EmptyState title={t('table.empty')} />;
  }

  return (
    <ExamPreviewTemplate
      exam={exam}
      questions={questions}
      currentIndex={currentIndex}
      onNavigate={setCurrentIndex}
      currentPreview={preview}
      isLoadingPreview={isLoadingPreview}
      languages={languages}
      selectedLangId={activeLangId}
      onLanguageChange={handleLanguageChange}
    />
  );
}
